import { useClientsQuery } from "@/query/clientQuery"
import { useBookingQuery } from "@/query/bookingQuery"
import { useServicesQuery } from "@/query/serviceQuery"
import {
  generateDashboardData,
  type CustomPeriod,
  type DashboardData,
  type PeriodType,
  type Service,
} from "@okeys/shared-utils"
import { useMemo } from "react"

type UseDashboardDataParams = {
  period?: PeriodType
  customPeriod?: CustomPeriod
}

/**
 * Hook personnalisé pour générer les données du dashboard
 */
export function useDashboardData(params?: UseDashboardDataParams): {
  dashboardData: DashboardData | null
  isLoading: boolean
} {
  const { booking: reservation, pendingBooking: pendingReservation } = useBookingQuery()
  const { clients, isLoading: isLoadingClients } = useClientsQuery()
  const { services, pendingServices } = useServicesQuery()

  const { period = "today", customPeriod } = params || {}

  const dashboardData = useMemo(() => {
    if (
      pendingReservation ||
      !reservation ||
      isLoadingClients ||
      !clients ||
      pendingServices ||
      !services
    ) {
      // Données par défaut en cas de chargement ou absence de données
      return {
        dashboardData: null,
        isLoading: true,
      }
    }

    // Map booking data to add price field (using total from BookingTable)
    const bookingWithPrice = (reservation || []).map((booking) => ({
      ...booking,
      price: booking.total || 0,
    }))

    return {
      dashboardData: generateDashboardData({
        booking: bookingWithPrice,
        clients: Array.isArray(clients) ? clients : [],
        services: Array.isArray(services)
          ? (services as Service<"beauty">[])
          : [],
        period,
        customPeriod,
      }),
      isLoading: false,
    }
  }, [
    reservation,
    pendingReservation,
    isLoadingClients,
    clients,
    pendingServices,
    services,
    period,
    customPeriod,
  ])

  return dashboardData
}
